import { useEffect, useState } from "react";
import { Form } from "antd";
import ActionModal from "components/Settings/components/ActionModal";
import CustomTextArea from "components/BaseWidgets/form/textArea";
import { mostLiked } from "components/Verification/helper";
import Tag from "components/BaseWidgets/tag";
import Rate from "components/BaseWidgets/rate";
import AlertNotification from "components/CustomWidgets/alertNotification";
import { useMutation } from "@tanstack/react-query";
import "./style.scss";
import { createFeedback } from "api/feedback";

function ReviewModal({ open, setOpen, onOk }) {
  const [form] = Form.useForm();
  const [activeTags, setActiveTags] = useState([]);

  const { mutate, isLoading } = useMutation({
    mutationFn: createFeedback,
  });

  const handleToggle = (tag) => {
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields(["rating", "feedback"]);

      if (!activeTags.length) {
        AlertNotification("warning", "Please select at least one tag");
        return;
      }

      const payload = {
        rating: values.rating,
        message: values.feedback,
        like_most: activeTags,
      };

      mutate(payload, {
        onSuccess: (data) => {
          if (data?.success) {
            setOpen(false);
            onOk();
            form.resetFields();
            setActiveTags([]);
          } else {
            AlertNotification("error", data?.message || "Something went wrong");
          }
        },
        onError: (error) => {
          const errorMsg =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to submit feedback";
          AlertNotification("error", errorMsg);
        },
      });
    } catch (errorInfo) {
      const firstError = errorInfo.errorFields?.[0]?.errors?.[0];
      AlertNotification(
        "warning",
        firstError || "Please fill all required fields",
      );
    }
  };

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        form.resetFields();
        setActiveTags([]);
      }, 0);
    }
  }, [open]);

  return (
    <ActionModal
      title="Rate your experience"
      description="Your feedback means the world to us! Share your thoughts, rate your experience, and help us improve our services to serve you better. Your insights make a difference!"
      className="review-modal"
      open={open}
      width={804}
      centerMode={false}
      setOpen={setOpen}
      footer={{
        left: { label: "Cancel", action: () => setOpen(false) },
        right: {
          label: "Submit",
          action: handleSubmit,
          loading: isLoading,
          disabled: isLoading,
        },
      }}
    >
      <Form form={form} layout="vertical">
        <div className="flex flex-col w-full gap-6 mt-6">
          {/* Rating */}
          <Form.Item
            name="rating"
            className="form-item m-0"
            rules={[
              {
                validator: (_, value) =>
                  value && value > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error("Please select a rating")),
              },
            ]}
            noStyle
          >
            <Rate />
          </Form.Item>

          {/* Tags */}
          <div className="flex flex-col gap-4">
            <label htmlFor="" className="input-label">
              Tell us what you like most
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {mostLiked.map((item, i) => (
                <Tag
                  key={i}
                  variant="selection"
                  isActive={activeTags.includes(item)}
                  onClick={() => handleToggle(item)}
                >
                  {item}
                </Tag>
              ))}
            </div>
          </div>

          {/* Feedback text */}
          <Form.Item
            name="feedback"
            className="form-item m-0"
            rules={[
              {
                required: true,
                message: "",
              },
            ]}
            noStyle
          >
            <CustomTextArea
              wrapperClass="gap-4"
              label="Tell us more..."
              placeholder="Start writing..."
              maxLength={300}
            />
          </Form.Item>
        </div>
      </Form>
    </ActionModal>
  );
}

export default ReviewModal;
